/**
 * useMapboxOverall
 * 
 * @Author: Focci
 * @Date: 2024-11-06 17:48:02
 * @Last Modified by: Focci
 * @Last Modified time: 2024-11-06 17:48:02
 */

// import PropTypes from 'prop-types'
import { useMapState } from '@comp/mapbox'
import { useMenuOptions, useLocationBarOptions } from '@comp/page/property/location/location-map-bar/lib'
import { useTranslations } from 'next-intl'
import { defaultConfig } from '@lib/theme'
import { useCallback, useMemo } from 'react'
import { checkLayerDisplay } from '@comp/mapbox/layer/lib'
import { filter, forEach, isArray, map, pick } from 'lodash-es'
import { markerMap, useMarkerCtrlOptions } from '../panel/MarkerController'

function getLegends({ mps, propertyOpts, schoolOpts, ts, hashMenus }) {
  const tmp = []
  
  // 房源
  const showPropertyLegend = filter(propertyOpts, (it) => mps[it.value])
  if(showPropertyLegend.length > 0) {
    tmp.push({
      label: ts('displayProperty'),
      alias: 'property',
      key: 'property',
      options: map(showPropertyLegend, (item) => ({
        label: item.label,
        legend: { type: 'circle', fill: defaultConfig.palette[item.color]?.main }
      }))
    })
  }
  
  // 学校
  if(mps.school && mps.schoolParam?.type?.length > 0) {
    tmp.push({
      label: ts('surroundingSchools'),
      alias: 'school',
      key: 'school',
      options: map(
        filter(schoolOpts, (it) => mps.schoolParam?.type?.indexOf(it.value) > -1),
        (item) => ({
          label: item.label,
          legend: { type: 'circle', fill: item.color }
        })
      )
    })
  }

  // 新开发
  const { display, cfg } = mps.layers
  forEach(display, (key) => {
    const opts = cfg?.[key]
    if(isArray(opts)) {
      const options = []
      forEach(opts, (opt) => {
        if(!checkLayerDisplay(mps.extra, opt.locations)) return
        forEach(opt.legend, (l) => {
          options.push(l.type === 'steps' ? l : { label: l.name, legend: pick(l, ['type', 'fill']) })
        })
      })

      tmp.push({
        options,
        key,
        label: hashMenus[key]?.label,
        subTitle: hashMenus[key]?.subTitle,
      })
    }
  })
  
  // 政府公屋
  if(mps.layers.display.indexOf('governmentHousing') > -1) {
    tmp.push({
      label: ts('governmentHousing'),
      key: 'governmentHousing',
      options: [
        { label: ts('governmentHousing'), legend: { type: 'circle', fill: '#720984' } }
      ]
    })
  }
  
  return tmp
}

export function useSchoolOptions() {
  const ts = useTranslations('search')

  return useMemo(
    () => [
      { label: ts('primarySchool'), color: '#5c5eff', value: '1' },
      { label: ts('middleSchool'), color: '#20a4f8', value: '2' },
      { label: ts('privateSchool'), color: '#9f17e0', value: '4' },
      { label: ts('kindergarten'), color: '#3ab765', value: '5' },
    ], 
    [ts]
  )
}

export default function useMapboxOverall({ type }) {
  const mps = useMapState()
  const ts = useTranslations('search')
  const { hashMenus } = useMenuOptions(mps.extra)

  /**
   * 菜单选项
   */
  const { options } = useLocationBarOptions(type)
  
  /**
   * 房源选项
   */
  const propertyOpts = useMarkerCtrlOptions(type)

  /**
   * 学校选项
   */
  const schoolOpts = useSchoolOptions()

  /**
   * legends
   */
  const legends = useMemo(
    () => getLegends({ mps, propertyOpts, schoolOpts, ts, hashMenus }), 
    [mps, propertyOpts, schoolOpts, ts, hashMenus]
  )

  // 清除所有图层
  const clearOptions = useCallback(() => {
    // 卫星图
    mps.valueSetter('satellite', false)
    // 清空房价热力图
    mps.valueSetter('avm', false)

    // 清除房源
    forEach(propertyOpts, ({ value }) => {
      value && mps.valueSetter(value, value === markerMap[type])
    })

    // 清除学校
    mps.valueSetter('school', false)
    mps.valueSetter('schoolParam', { ...mps.schoolParam, type: [] })
    
    // 政府公屋
    mps.valueSetter('stateHouse', false)

    // 清空新开发
    mps.setLayers((prev) => ({ ...prev, display: [] }))
  }, [mps, propertyOpts, type])

  return {
    legends,
    propertyOpts,
    schoolOpts,
    options,
    clearOptions,
  }
}
